import React from 'react';
import { EngagementSpotlight } from '@atlassiansox/engagekit-ts';
import { graphql, useFragment } from 'react-relay';
import { SpotlightManager, SpotlightTarget } from '@atlaskit/onboarding';
import {
	type Project,
	ProjectStyle,
	TEAM_MANAGED_PROJECT,
} from '@atlassian/jira-common-constants/src/project-types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIsFullscreen } from '@atlassian/jira-layout-controller/src/controllers/layout-controller/consumers/fullscreen/index.tsx';
import type { extraComponentsFar_horizontalNav$key } from '@atlassian/jira-relay/src/__generated__/extraComponentsFar_horizontalNav.graphql';
import { useIsAnonymous } from '@atlassian/jira-tenant-context-controller/src/components/is-anonymous/index.tsx';
import { useCurrentRoute } from '@atlassian/jira-platform-router-utils/src/index.tsx';
import { ShareButton } from '../share-button/index.tsx';
import { HeaderFullscreenButton, SUPPORTED_ROUTES } from '../fullscreen-button/index.tsx';
import { AutomationButton } from './automation-button/AutomationButton.tsx';

type ExtraComponentProps = {
	projectFragment: extraComponentsFar_horizontalNav$key | null;
};

// Components that go on the far right side of the header
export const ExtraComponentsFar = ({ projectFragment }: ExtraComponentProps) => {
	const [isFullscreen] = useIsFullscreen();
	const { name: currentRouteName } = useCurrentRoute();
	const isAnonymous = useIsAnonymous();
	const componentsToShow: React.ReactNode[] = [];

	const project = useFragment(
		graphql`
			fragment extraComponentsFar_horizontalNav on JiraProject {
				key
				name
				projectId
				projectStyle
				projectType
				avatar {
					large
				}
				canEditProjectConfig: action(type: EDIT_PROJECT_CONFIG) {
					canPerform
				}
			}
		`,
		projectFragment,
	);

	const hasFullscreenButton = SUPPORTED_ROUTES.includes(currentRouteName);

	const canEditProjectConfig = project?.canEditProjectConfig?.canPerform ?? false;
	const shouldShowAutomation = !isAnonymous && project && canEditProjectConfig;
	const isSimplified = project?.projectStyle === TEAM_MANAGED_PROJECT;

	if (isFullscreen) {
		if (hasFullscreenButton && fg('blu-5747-fullscreen-in-nav-header')) {
			componentsToShow.push(<HeaderFullscreenButton key="fullscreen" />);
			return componentsToShow;
		}
		return null;
	}

	let projectShareContext: Project | null = null;
	if (project) {
		projectShareContext = {
			avatarUrl: project?.avatar?.large ?? '',
			id: Number(project.projectId),
			isSimplified,
			key: project.key,
			name: project.name,
			style:
				project.projectStyle === TEAM_MANAGED_PROJECT
					? ProjectStyle.NEXT_GEN
					: ProjectStyle.CLASSIC,
			type: project?.projectType ?? '',
		};
	}

	if (hasFullscreenButton && fg('blu-5747-fullscreen-in-nav-header')) {
		componentsToShow.push(<HeaderFullscreenButton key="fullscreen" />);
	}

	if (projectShareContext && !isAnonymous) {
		componentsToShow.push(
			<SpotlightManager key="share-spotlight">
				<SpotlightTarget name="ccEcoBoardBacklogShare">
					<ShareButton data={projectShareContext} key="Share" />
				</SpotlightTarget>
				<EngagementSpotlight name="ccEcoBoardBacklogShare" />
			</SpotlightManager>,
		);
	}

	if (shouldShowAutomation) {
		componentsToShow.push(<AutomationButton key="automation" />);
	}

	return componentsToShow;
};
